/**
 * AddToCategoryDialog
 * Assigns a protocol, protocol draft or knowledge source to a category/subcategory
 * (find-or-create). Opens on the item's current category when it has one.
 *
 * Deliberately sourced from the CS Categories catalog, NOT the Pulse-derived tree the
 * Sources sidebar uses — the catalog is the canonical, curated set of category/subcategory
 * names content gets tagged with, independent of what Pulse happened to see in tickets.
 */

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CategoryCombobox } from '@/modules/sop';
import { usePublishedCSCategories } from '@/hooks';

interface AddToCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting?: boolean;
  onSubmit: (category: string, subcategory: string) => void | Promise<void>;
  /** The item's current filing, which the dialog opens on. */
  initialCategory?: string;
  initialSubcategory?: string;
  title?: string;
  description?: string;
}

export function AddToCategoryDialog({
  open,
  onOpenChange,
  isSubmitting,
  onSubmit,
  initialCategory = '',
  initialSubcategory = '',
  title = 'Add to category ?',
  description = 'A source can have multiple category and subcategory tags'
}: AddToCategoryDialogProps) {
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');

  const { data: csCategories = [] } = usePublishedCSCategories();

  useEffect(() => {
    if (open) {
      setCategory(initialCategory);
      setSubcategory(initialSubcategory);
    }
  }, [open, initialCategory, initialSubcategory]);

  const categoryOptions = Array.from(new Set(csCategories.map((c) => c.category).filter(Boolean))).sort();
  const subcategoryOptions = category
    ? Array.from(
        new Set(csCategories.filter((c) => c.category === category).map((c) => c.subcategory).filter(Boolean))
      ).sort()
    : [];

  const canSubmit = category.trim().length > 0 && subcategory.trim().length > 0;

  const handleCategoryChange = (value: string) => {
    setCategory(value);
    // Changing category invalidates whatever subcategory was picked under the old one.
    setSubcategory('');
  };

  const handleAdd = async () => {
    if (!canSubmit) return;
    await onSubmit(category.trim(), subcategory.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-md p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-semibold'>{title}</DialogTitle>
          <p className='text-xs text-muted-foreground'>{description}</p>
        </DialogHeader>

        <div className='flex flex-col gap-4'>
          <div>
            <p className='text-xs font-medium mb-1.5 text-gray-700'>Choose Category</p>
            <CategoryCombobox value={category} onChange={handleCategoryChange} options={categoryOptions} placeholder='Enter display Title' />
          </div>
          <div>
            <p className='text-xs font-medium mb-1.5 text-gray-700'>Choose Sub-Category</p>
            <CategoryCombobox
              value={subcategory}
              onChange={setSubcategory}
              options={subcategoryOptions}
              placeholder='Enter display Title'
              lockedMessage={!category ? 'Please select a category first' : undefined}
            />
          </div>
        </div>

        <DialogFooter className='mt-2'>
          <Button type='button' variant='outline' size='sm' className='text-xs' onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type='button' size='sm' className='text-xs' onClick={handleAdd} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? 'Saving…' : initialCategory ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
